import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  DialogHostMutationContext,
  DialogHostStackContext,
} from "./DialogHostMutationContext";
import { DIALOG_EXIT_MS } from "./Internal/dialogConstants";

// Bridge JS↔CSS for the exit duration once at module load. The CSS keyframes
// in index.css read `var(--dialog-exit-duration, 130ms)`; the fallback covers
// the brief window before this code runs (and SSR). DIALOG_EXIT_MS in
// dialogConstants is the single source of truth — bumping it updates both
// the CSS animation duration and the JS slot-cleanup timeout.
if (typeof document !== "undefined") {
  document.documentElement.style.setProperty(
    "--dialog-exit-duration",
    `${DIALOG_EXIT_MS}ms`,
  );
}

/**
 * Mount point for dialog exit-animation ghosts and shared dialog state.
 * Sits near the app root so dialogs render outside any
 * Page/ControlBar/Menu/Table context — preventing inherited contexts from
 * breaking button styling or component behavior.
 *
 * Owns three sources of truth:
 *
 * 1. `slots` (Map<id, ReactNode>) — exit-animation ghosts. A slot is filled
 *    only when DialogModal_ is unmounted while open=true: the cleanup pushes
 *    a closed snapshot of DialogInner_ that survives the parent unmount,
 *    plays the exit animation, then clears after DIALOG_EXIT_MS. The normal
 *    open/close path doesn't touch this map.
 * 2. `stack` (string[]) — open order across all live dialogs, drives
 *    per-dialog z-index.
 * 3. `bodyLockSet` (Set<id>) — dialogs that want the body scroll locked.
 *    Centralized here so nested dialogs don't fight over body.style.overflow:
 *    when an inner dialog closes its lock comes off the set but the outer's
 *    is still there, so body stays locked.
 *
 * The two contexts (Mutation vs Stack) are split so consumers that only
 * need to call mount/unmount/register don't re-render when the stack
 * changes.
 */
export function DialogHost({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<Map<string, ReactNode>>(new Map());
  const [stack, setStack] = useState<string[]>([]);
  const bodyLockSetRef = useRef<Set<string>>(new Set());

  const mount = useCallback((id: string, element: ReactNode) => {
    setSlots((prev) => new Map(prev).set(id, element));
  }, []);

  const unmount = useCallback((id: string) => {
    setSlots((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const register = useCallback((id: string) => {
    setStack((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const unregister = useCallback((id: string) => {
    setStack((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev,
    );
  }, []);

  const setBodyLock = useCallback((id: string, locking: boolean) => {
    const set = bodyLockSetRef.current;
    const before = set.size;
    if (locking) set.add(id);
    else set.delete(id);
    const after = set.size;
    if (before === 0 && after > 0) {
      document.body.style.overflow = "hidden";
    } else if (before > 0 && after === 0) {
      document.body.style.removeProperty("overflow");
    }
  }, []);

  // Failsafe: if DialogHost itself unmounts while any locks remain, restore.
  useEffect(() => {
    return () => {
      if (bodyLockSetRef.current.size > 0) {
        document.body.style.removeProperty("overflow");
      }
    };
  }, []);

  const mutations = useMemo(
    () => ({
      mount,
      unmount,
      register,
      unregister,
      setBodyLock,
      mounted: true,
    }),
    [mount, unmount, register, unregister, setBodyLock],
  );

  return (
    <DialogHostMutationContext.Provider value={mutations}>
      <DialogHostStackContext.Provider value={stack}>
        {children}
        {[...slots.entries()].map(([id, el]) => (
          <Fragment key={id}>{el}</Fragment>
        ))}
      </DialogHostStackContext.Provider>
    </DialogHostMutationContext.Provider>
  );
}
